/**
 * Contexto del pedido (Operaciones). Carga UNA vez los datos núcleo del pedido
 * (order + cod + líneas + pagos + envíos + documentos) y los comparte con el hub y sus
 * páginas vinculadas (Entrega/Pagos/Documentos) para no re-fetchear al navegar entre ellas.
 *
 * Expone:
 *  - reload(): recarga núcleo + bump de reloadKey (refresca cronología/derivados).
 *  - setOrder/setCod: setters para mutaciones OPTIMISTAS con rollback (ej. edición de cliente),
 *    sin filtrar el estado interno de cada página.
 *
 * Datos page-specific (imágenes/costos de líneas, comprobantes de pago) NO van acá: cada
 * página los deriva por su cuenta reaccionando a `lines`/`payments`.
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode, type Dispatch, type SetStateAction } from 'react';
import { useAuth } from '@smarty/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminToast } from '@/contexts/AdminToastContext';
import {
  fetchOrderByKey,
  fetchOrderLines,
  fetchOrderCodProfile,
  fetchOrderPayments,
  fetchOrderShipments,
  fetchOrderDocuments,
  type OrderPayment,
  type OrderShipment,
  type OrderCodProfile,
  type OrderDocument,
} from '@/lib/operations/ordersApi';
import { previoDeVentaPorClave } from '@/lib/dropshipping/datosDeDrop';
import { elDestinoDelPaquete, elPaqueteDelEncargo, type PaqueteDelEncargo } from '@/lib/dropshipping/elPaqueteDelEncargo';
import { adelantarLaEntregaElegible } from '@/lib/dropshipping/couriersDelDrop';
import { loadUbigeo } from '@/lib/ubigeo';
import type { OrderRecord, OrderLine } from '@/types/operations.types';
import { CLAVES_DROP } from '@/lib/dropshipping/clavesDeCache';
import { alcanceDelPedido, type AlcanceDelPedido } from '@/lib/operations/alcanceDelPedido';

export interface OrderContextValue {
  order: OrderRecord | null;
  cod: OrderCodProfile | null;
  lines: OrderLine[];
  payments: OrderPayment[];
  shipments: OrderShipment[];
  documents: OrderDocument[];
  loading: boolean;
  /** Se incrementa en cada carga; útil como refreshKey de la cronología. */
  reloadKey: number;
  reload: () => Promise<void>;
  setOrder: Dispatch<SetStateAction<OrderRecord | null>>;
  setCod: Dispatch<SetStateAction<OrderCodProfile | null>>;
  /** Qué puede hacer quien mira con ESTE pedido. Ver `alcanceDelPedido`. */
  alcance: AlcanceDelPedido;
  /** En un encargo de Smarty Drop, la venta de la tienda: donde vive el envío. Ver `elPaqueteDelEncargo`. */
  paquete: PaqueteDelEncargo | null;
  /** `cod` con la dirección del paquete encima: a dónde va de verdad. Lo que muestra o imprime el destino lee esto. */
  destino: OrderCodProfile | null;
}

const OrderContext = createContext<OrderContextValue | null>(null);

export function useOrder(): OrderContextValue {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error('useOrder() debe usarse dentro de <OrderProvider>.');
  return ctx;
}

/** El alcance del pedido abierto, o null fuera de un pedido (las listas, los ajustes). */
export function useAlcanceDelPedido(): AlcanceDelPedido | null {
  return useContext(OrderContext)?.alcance ?? null;
}

export function OrderProvider({ id, children }: { id: string; children: ReactNode }) {
  const { tenantId, loading: authLoading } = useAuth();
  const toast = useAdminToast();

  const [order, setOrder] = useState<OrderRecord | null>(null);
  const [cod, setCod] = useState<OrderCodProfile | null>(null);
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [payments, setPayments] = useState<OrderPayment[]>([]);
  const [shipments, setShipments] = useState<OrderShipment[]>([]);
  const [documents, setDocuments] = useState<OrderDocument[]>([]);
  const [paquete, setPaquete] = useState<PaqueteDelEncargo | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  // `toast` (AdminToastContext) tiene identidad NUEVA en cada render (su value no está
  // memoizado). Lo leemos por ref para que `reload` NO dependa de `toast` y sea ESTABLE:
  // si dependiera, cada toast (mostrar/auto-cerrar) recrearía `reload` y el useEffect de
  // abajo dispararía un refetch que PISA el estado optimista (bug: el cliente se asigna →
  // se quita → reaparece a los segundos). `reload` se pasa por contexto a muchos consumidores,
  // así que su estabilidad importa.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  /*
   * ⭐ EL PREVIO DEL ALMACÉN SE PIDE A LA VEZ QUE LA VENTA, no después.
   *
   *    El founder: «esta opción de pagar almacén demora en aparecer, ¿a qué se debe?».
   *
   * 🪤 NO ERA LA BASE. Medido en producción: el previo tarda 5,8 ms y resolver la venta 0,3 ms.
   *    Eran dos VIAJES DE RED en fila — la tarjeta vive dentro de la página, la página no se dibuja
   *    hasta que llega la venta, y recién ahí la tarjeta podía empezar a pedir lo suyo.
   *
   * 🪤 Y ARREGLARLO DENTRO DE LA TARJETA NO ALCANZABA: por más que sepa pedir por el número de la
   *    URL, se monta tarde. Tenía que salir desde acá, que es lo que corre PRIMERO y ya conoce la
   *    clave. Cuando la página se dibuja, la respuesta ya está en la caché y el botón aparece con
   *    todo lo demás.
   *
   * 🪤 `prefetchQuery` y no `fetchQuery`: esto es una conveniencia. Si falla —una venta que no es
   *    de Smarty Drop, un permiso— no puede tumbar la carga del pedido, que es lo importante.
   */
  const queryClient = useQueryClient();
  useEffect(() => {
    if (authLoading || !tenantId || !id) return;
    void queryClient.prefetchQuery({
      queryKey: [CLAVES_DROP.previoDeVenta, id],
      queryFn: () => previoDeVentaPorClave(id, tenantId),
      staleTime: 30_000,
    });
    /*
     * ⭐ EL UBIGEO, YA. Es el catálogo de los selects de departamento, provincia y distrito (~2.100 filas, una vez
     *    por sesión). Pedido recién al abrir el modal de la dirección, el modal mostraba «Elige un departamento»
     *    unos segundos (founder, 2-oct). Si ya está, no sale a la red.
     */
    void loadUbigeo().catch(() => { /* el modal lo vuelve a pedir */ });
  }, [authLoading, tenantId, id, queryClient]);

  const reload = useCallback(async () => {
    if (!tenantId || !id) return;
    try {
      // `id` puede ser el número de pedido (URL legible) o el UUID (links viejos): se resuelve.
      // Las entidades hijas se cargan por el UUID real (order.id), no por la clave de la URL.
      const o = await fetchOrderByKey(tenantId, id);
      if (!o) {
        setOrder(null); setLines([]); setPayments([]); setShipments([]); setCod(null); setDocuments([]); setPaquete(null);
        setReloadKey((k) => k + 1);
        return;
      }
      // Las partes del pedido son de SU cuenta, no de la de quien mira: un encargo ajeno se abre por
      // la parte que uno tiene en él, y la base (RLS) decide qué de eso se puede leer.
      const duenoDelPedido = o.tenant_id;
      const envios = fetchOrderShipments(duenoDelPedido, o.id);
      const elPaquete = elPaqueteDelEncargo(o);
      // Los días de GSG salen a la vez que el resto, no cuando la tarjeta se dibuja (`adelantarLaEntregaElegible`).
      // Si algo de esto falla, lo dice la carga de abajo: acá no hay nada que avisar.
      void Promise.all([envios, elPaquete]).then(([s, paq]) => adelantarLaEntregaElegible(queryClient, paq?.order ?? o, s)).catch(() => {});
      const [l, p, s, c, d, paq] = await Promise.all([
        fetchOrderLines(duenoDelPedido, o.id), fetchOrderPayments(duenoDelPedido, o.id), envios,
        fetchOrderCodProfile(duenoDelPedido, o.id), fetchOrderDocuments(duenoDelPedido, o.id), elPaquete,
      ]);
      setOrder(o); setLines(l); setPayments(p); setShipments(s); setCod(c); setDocuments(d); setPaquete(paq);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toastRef.current.error('Error', err instanceof Error ? err.message : 'No se pudo cargar el pedido.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, id, queryClient]);

  useEffect(() => { if (!authLoading && tenantId) reload(); }, [authLoading, tenantId, reload]);
  const destino = useMemo(() => elDestinoDelPaquete(cod, paquete), [cod, paquete]);

  return (
    <OrderContext.Provider
      value={{ order, cod, lines, payments, shipments, documents, loading, reloadKey, reload, setOrder, setCod, alcance: alcanceDelPedido(order, tenantId), paquete, destino }}
    >
      {children}
    </OrderContext.Provider>
  );
}
